import {
    provideHttpClient,
    withInterceptors,
    withNoXsrfProtection,
} from '@angular/common/http';
import {
    APP_ID,
    ApplicationConfig,
    ErrorHandler,
    provideBrowserGlobalErrorListeners,
    provideZonelessChangeDetection,
} from '@angular/core';
import {
    MAT_LUXON_DATE_ADAPTER_OPTIONS,
    MAT_LUXON_DATE_FORMATS,
    provideLuxonDateAdapter,
} from '@angular/material-luxon-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import {
    provideClientHydration,
    withIncrementalHydration,
    withNoHttpTransferCache,
} from '@angular/platform-browser';
import {
    provideRouter,
    withAutoCleanupInjectors,
    withComponentInputBinding,
    withInMemoryScrolling,
    withPreloading,
    withRouterConfig,
    withViewTransitions,
} from '@angular/router';
import { authInterceptor } from '@core/interceptors/auth.interceptor';
import { sentryHttpInterceptor } from '@core/interceptors/sentry-http.interceptor';
import {
    HttpError,
    httpErrorExtractor,
} from '@core/sentry/http-error-extractor';
import { DjangoReloadService } from '@core/services/django-reload.service.interface';
import { NoopDjangoReloadService } from '@core/services/django-reload.service.noop';
import { EnvironmentService } from '@core/services/environment.service';
import {
    LOCALSTORAGE,
    LocalStorageService,
} from '@core/services/local-storage.service';
import { WINDOW, WindowService } from '@core/services/window.service';
import { provideBuildInformation } from '@core/utils/buildProvider';
import { environment } from '@environment/environment';
import * as Sentry from '@sentry/angular';
import { quicklinkProviders, QuicklinkStrategy } from 'ngx-quicklink';
import { appRoutes } from './app.routes';

/**
 * The adapter's `'D'` is the locale's numeric date, which admins in different
 * countries read as different days. The month is written out instead; typing
 * still accepts the numeric form.
 *
 * Provided at the root, so no component may import `MatLuxonDateModule` —
 * its providers carry the defaults and would shadow these.
 */
const DATE_FORMATS_WITH_WRITTEN_MONTH = {
    ...MAT_LUXON_DATE_FORMATS,
    parse: {
        ...MAT_LUXON_DATE_FORMATS.parse,
        dateInput: ['D', 'd MMM yyyy'],
    },
    display: {
        ...MAT_LUXON_DATE_FORMATS.display,
        dateInput: 'dd MMM yyyy',
    },
};

export const appConfig: ApplicationConfig = {
    providers: [
        { provide: APP_ID, useValue: 'angular-universal' },
        {
            provide: WINDOW,
            useFactory: (windowService: WindowService) =>
                windowService.nativeWindow,
            deps: [WindowService],
        },
        {
            provide: LOCALSTORAGE,
            useFactory: (localStorageService: LocalStorageService) =>
                localStorageService.localStorage,
            deps: [LocalStorageService],
        },
        { provide: MAT_LUXON_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
        { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
        provideLuxonDateAdapter(DATE_FORMATS_WITH_WRITTEN_MONTH),
        provideBuildInformation(),
        { provide: DjangoReloadService, useClass: NoopDjangoReloadService },
        {
            provide: ErrorHandler,
            useFactory: (environmentService: EnvironmentService) => {
                Sentry.init({
                    dsn: environmentService.isDisposable()
                        ? undefined
                        : environment.sentryBrowserDSN,
                    environment: environmentService.getEnvironmentName(),
                    release: environmentService.getVersion(),
                    // Session Replay: only capture replays on errors in /office/ routes
                    replaysSessionSampleRate: 0,
                    replaysOnErrorSampleRate: environmentService.isBrowser()
                        ? 1.0
                        : 0,
                    integrations: [
                        // Disabled rather than removed: its setup raises
                        // Error.stackTraceLimit from 10 to 50.
                        Sentry.globalHandlersIntegration({
                            onerror: false,
                            onunhandledrejection: false,
                        }),
                    ],
                    ignoreErrors: [
                        // Ignore third-party script loading failures
                        /Couldn't load script https:\/\/script\.tapfiliate\.com/,
                        /Couldn't load script https:\/\/www\.googletagmanager\.com\/gtm\.js/,
                    ],
                    beforeSend: (event, hint) => {
                        const error = hint.originalException;
                        if (error instanceof HttpError) {
                            event.contexts = {
                                ...event.contexts,
                                request: {
                                    url: error.url,
                                    method: error.requestMethod,
                                    data: error.requestBody,
                                },
                                response: {
                                    status_code: error.status,
                                    data: error.responseBody,
                                },
                            };
                        }
                        return event;
                    },
                });
                return Sentry.createErrorHandler({
                    extractor: httpErrorExtractor,
                });
            },
            deps: [EnvironmentService, WindowService],
        },
        provideClientHydration(
            withNoHttpTransferCache(),
            withIncrementalHydration()
        ),
        provideHttpClient(
            // The backend runs no CSRF check, so the interceptor would only
            // hunt for a cookie nothing sets. SameSite=Strict is the defence.
            withNoXsrfProtection(),
            withInterceptors([authInterceptor, sentryHttpInterceptor])
        ),
        quicklinkProviders,
        provideBrowserGlobalErrorListeners(),
        provideZonelessChangeDetection(),
        provideRouter(
            appRoutes,
            withPreloading(QuicklinkStrategy),
            withInMemoryScrolling({
                scrollPositionRestoration: 'enabled',
                anchorScrolling: 'enabled',
            }),
            withRouterConfig({
                paramsInheritanceStrategy: 'always',
                onSameUrlNavigation: 'reload',
            }),
            withViewTransitions(),
            withComponentInputBinding(),
            withAutoCleanupInjectors()
        ),
    ],
};
